// Internal Dashboard Analytics (Prompt 2) — visual analytics, trends,
// distributions and a client league table, all from real backend aggregations.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
    TrendingUp, TrendingDown, Minus, CheckSquare, AlertTriangle, ShieldAlert,
    Gauge, ArrowUpRight, Trophy, FolderOpen, ChevronDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { useView } from "@/context/ViewContext";

const ACCENT = "#C75A1B";
const ACCENT_DARK = "#9F4212";
const SEV_COLORS = { Minor: "#3F7D3A", Moderate: "#C68A1C", Major: "#D9772E", Fatal: "#B33A2E" };
const PRIORITY_COLORS = { Low: "#6F6A64", Medium: "#2A5DB0", High: "#C68A1C", Critical: "#B33A2E" };
const STATUS_PALETTE = ["#C75A1B", "#2A5DB0", "#C68A1C", "#3F7D3A", "#B33A2E", "#6B4E9E", "#6F6A64"];
const STAGE_LABELS = {
    enquiry: "Enquiry", setup: "Setup", pre_construction: "Pre-Con", site_visits: "Site Visits",
    reporting: "Reporting", qa: "QA", client_issue: "Client Issue", completion: "Completion",
};
const STAGE_ORDER = ["enquiry", "setup", "pre_construction", "site_visits", "reporting", "qa", "client_issue", "completion"];

function toPairs(obj, order) {
    const entries = Object.entries(obj || {});
    if (order) entries.sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
    return entries;
}

export default function AnalyticsPage() {
    const [months, setMonths] = useState(6);
    const [projLeagueOpen, setProjLeagueOpen] = useState(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const { enterPortal, portalClientId, isInternalView } = useView();

    useEffect(() => {
        setData(null);
        const params = { months };
        // Internal staff previewing a client's portal ("View as") must see the
        // same client-scoped analytics that client would see — never the full
        // cross-tenant view. Real portal users are always scoped server-side
        // regardless of this param.
        if (portalClientId && !isInternalView) params.client_id = portalClientId;
        api.get("/dashboard/analytics", { params })
            .then((r) => { setData(r.data); setError(false); })
            .catch(() => setError(true));
    }, [months, portalClientId, isInternalView]);

    if (error) {
        return (
            <div data-testid="analytics-denied" className="font-serif-italic text-[#9A968F] py-12 text-center">
                Analytics is available to Priority Risk internal staff only.
            </div>
        );
    }
    if (!data) {
        return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading analytics…</div>;
    }

    const h = data.headline;
    const isPortal = data.is_portal;
    const projOpen = projLeagueOpen === null ? isPortal : projLeagueOpen;
    const drillClient = (cid) => { enterPortal(cid); navigate("/app/dashboard"); };
    const drillProject = (pid) => navigate(`/app/projects/${pid}`);
    const stagePairs = toPairs(data.distributions.projects_by_stage, STAGE_ORDER);

    return (
        <div data-testid="analytics-page" className="space-y-7">
            {/* Header */}
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: `2px solid ${ACCENT}`, color: "#6F6A64" }}>
                        {isPortal ? "Portfolio · Insights" : "Internal · Insights"}
                    </span>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#1F1F1F]">Analytics &amp; trends</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {isPortal
                            ? "Live analytics across your projects · click any chart or row to drill in"
                            : "Live operational analytics across the estate · click any chart or row to drill in"}
                    </p>
                </div>
                <div data-testid="analytics-range" className="inline-flex rounded-[9px] border overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    {[3, 6, 12].map((m) => (
                        <button key={m} data-testid={`analytics-range-${m}`} onClick={() => setMonths(m)}
                            className={`px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${months === m ? "text-white" : "text-[#6F6A64] hover:bg-[#F6F4F0]"}`}
                            style={months === m ? { background: ACCENT } : undefined}>
                            {m}m
                        </button>
                    ))}
                </div>
            </div>

            {/* Headline metrics */}
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))" }}>
                <HeadlineCard testid="headline-actions-completed" icon={CheckSquare} tone="green"
                    label="Actions completed this month" value={h.actions_completed.value} delta={h.actions_completed.delta} />
                <HeadlineCard testid="headline-completion-rate" icon={Gauge} tone="accent"
                    label="Action completion rate" value={`${h.completion_rate}%`} />
                <HeadlineCard testid="headline-overdue" icon={AlertTriangle} tone="amber"
                    label="Overdue actions" value={h.overdue_actions} />
                <HeadlineCard testid="headline-riddor" icon={ShieldAlert} tone="red"
                    label="RIDDOR open" value={h.riddor_open} />
                <HeadlineCard testid="headline-incidents" icon={AlertTriangle} tone="neutral"
                    label="Incidents this month" value={h.incidents.value} delta={h.incidents.delta} invertDelta />
                <HeadlineCard testid="headline-open-actions" icon={CheckSquare} tone="neutral"
                    label="Open actions (estate)" value={h.open_actions} />
            </div>

            {/* Trends */}
            <div className="grid lg:grid-cols-2 gap-5">
                <ChartCard testid="chart-actions-trend" title="Actions: created vs completed" subtitle={`Last ${months} months`}>
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={data.trends.actions} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={ACCENT} stopOpacity={0.32} />
                                    <stop offset="100%" stopColor={ACCENT} stopOpacity={0.02} />
                                </linearGradient>
                                <linearGradient id="gCompleted" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#3F7D3A" stopOpacity={0.28} />
                                    <stop offset="100%" stopColor="#3F7D3A" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={TT} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Area type="monotone" dataKey="created" name="Created" stroke={ACCENT} strokeWidth={2} fill="url(#gCreated)" />
                            <Area type="monotone" dataKey="completed" name="Completed" stroke="#3F7D3A" strokeWidth={2} fill="url(#gCompleted)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard testid="chart-incidents-trend" title="Incidents by severity" subtitle={`Last ${months} months`}>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={data.trends.incidents} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={TT} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {["Minor", "Moderate", "Major", "Fatal"].map((s) => (
                                <Bar key={s} dataKey={s} name={s} stackId="sev" fill={SEV_COLORS[s]} radius={[2, 2, 0, 0]} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard testid="chart-visits-trend" title="Site visits: scheduled vs completed" subtitle={`Last ${months} months`}>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={data.trends.visits} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={TT} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="scheduled" name="Scheduled" fill="#2A5DB0" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="completed" name="Completed" fill="#3F7D3A" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard testid="chart-projects-stage" title="Projects by workflow stage" subtitle="Click a bar to open that stage">
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={stagePairs.map(([k, v]) => ({ key: k, label: STAGE_LABELS[k] || k, count: v }))}
                            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6F6A64" }} axisLine={false} tickLine={false} interval={0} angle={-18} textAnchor="end" height={48} />
                            <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip contentStyle={TT} cursor={{ fill: "#FBEFE5" }} />
                            <Bar dataKey="count" name="Projects" radius={[3, 3, 0, 0]} cursor="pointer"
                                onClick={(d) => d?.key && navigate(`/app/projects?stage=${d.key}`)}>
                                {stagePairs.map(([k], i) => (
                                    <Cell key={k} fill={i % 2 ? ACCENT_DARK : ACCENT} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            {/* Distributions row */}
            <div className="grid lg:grid-cols-3 gap-5">
                <ChartCard testid="chart-actions-priority" title="Open actions by priority">
                    <ResponsiveContainer width="100%" height={210}>
                        <BarChart layout="vertical"
                            data={["Critical", "High", "Medium", "Low"].map((p) => ({ name: p, count: data.distributions.actions_by_priority?.[p] || 0 }))}
                            margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} width={62} />
                            <Tooltip contentStyle={TT} cursor={{ fill: "#FBEFE5" }} />
                            <Bar dataKey="count" name="Open" radius={[0, 3, 3, 0]} cursor="pointer" onClick={() => navigate("/app/actions")}>
                                {["Critical", "High", "Medium", "Low"].map((p) => <Cell key={p} fill={PRIORITY_COLORS[p]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <DonutCard testid="chart-actions-status" title="Actions by status"
                    pairs={toPairs(data.distributions.actions_by_status)} onClick={() => navigate("/app/actions")} />

                <DonutCard testid="chart-docs-status" title="Documents by status"
                    pairs={toPairs(data.distributions.documents_by_status)} onClick={() => navigate("/app/documents")} />
            </div>

            {/* Client league table (internal only) — shown first for PR staff */}
            {!isPortal && (
            <div data-testid="analytics-league" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4" style={{ color: ACCENT }} />
                        <div>
                            <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Client league table</h3>
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Ranked by outstanding risk · click a row to open the portal</p>
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12.7px]">
                        <thead>
                            <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                <th className="text-left px-5 py-2.5 font-bold">#</th>
                                <th className="text-left px-3 py-2.5 font-bold">Client</th>
                                <th className="text-right px-3 py-2.5 font-bold">Active projects</th>
                                <th className="text-right px-3 py-2.5 font-bold">Open actions</th>
                                <th className="text-right px-3 py-2.5 font-bold">Overdue</th>
                                <th className="text-right px-3 py-2.5 font-bold">Open incidents</th>
                                <th className="text-right px-3 py-2.5 font-bold">RIDDOR</th>
                                <th className="text-left px-3 py-2.5 font-bold w-[160px]">Completion rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.league.map((c, i) => (
                                <tr key={c.client_id} data-testid={`league-row-${c.slug || c.client_id}`}
                                    onClick={() => drillClient(c.client_id)}
                                    className="border-t hover:bg-[#FCFBF9] transition-colors cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                    <td className="px-5 py-3 font-bold text-[#9A968F]">{i + 1}</td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-2.5">
                                            <span className="w-7 h-7 rounded-[6px] grid place-items-center text-[10px] font-bold text-white" style={{ background: c.colour }}>
                                                {c.initials}
                                            </span>
                                            <span className="font-semibold text-[#1F1F1F]">{c.name}</span>
                                            <ArrowUpRight className="w-3 h-3 text-[#C7C2BB]" />
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-right text-[#2D2D2D]">{c.active_projects}</td>
                                    <td className="px-3 py-3 text-right text-[#2D2D2D]">{c.open_actions}</td>
                                    <td className="px-3 py-3 text-right font-semibold" style={{ color: c.overdue_actions ? "#B33A2E" : "#6F6A64" }}>{c.overdue_actions}</td>
                                    <td className="px-3 py-3 text-right text-[#2D2D2D]">{c.open_incidents}</td>
                                    <td className="px-3 py-3 text-right font-semibold" style={{ color: c.riddor_open ? "#B33A2E" : "#6F6A64" }}>{c.riddor_open}</td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1.5 rounded-full bg-[#EFEAE4] overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${c.completion_rate}%`, background: c.completion_rate >= 60 ? "#3F7D3A" : c.completion_rate >= 30 ? "#C68A1C" : "#B33A2E" }} />
                                            </div>
                                            <span className="text-[11px] font-semibold w-8 text-right" style={{ color: "#6F6A64" }}>{c.completion_rate}%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {data.league.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-8 text-center italic text-[#9A968F]">No client data yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* Project league table (collapsible) */}
            <div data-testid="analytics-project-league" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <button type="button" data-testid="project-league-toggle" onClick={() => setProjLeagueOpen(!projOpen)}
                    className="w-full px-5 py-3.5 flex items-center justify-between border-b text-left hover:bg-[#FCFBF9] transition-colors" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4" style={{ color: ACCENT }} />
                        <div>
                            <h3 className="text-[14px] font-semibold text-[#1F1F1F] flex items-center gap-2">
                                Project league table
                                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{data.project_league.length}</span>
                            </h3>
                            <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Ranked by outstanding risk · click a row to open the project</p>
                        </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-[#9A968F] transition-transform shrink-0" style={{ transform: projOpen ? "rotate(180deg)" : "none" }} />
                </button>
                {projOpen && (
                <div className="overflow-x-auto">
                    <table className="w-full text-[12.7px]">
                        <thead>
                            <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                <th className="text-left px-5 py-2.5 font-bold">#</th>
                                <th className="text-left px-3 py-2.5 font-bold">Project</th>
                                <th className="text-left px-3 py-2.5 font-bold">Stage</th>
                                <th className="text-right px-3 py-2.5 font-bold">Open actions</th>
                                <th className="text-right px-3 py-2.5 font-bold">Overdue</th>
                                <th className="text-right px-3 py-2.5 font-bold">Open incidents</th>
                                <th className="text-right px-3 py-2.5 font-bold">RIDDOR</th>
                                <th className="text-left px-3 py-2.5 font-bold w-[160px]">Completion rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.project_league.map((p, i) => (
                                <tr key={p.project_id} data-testid={`project-league-row-${p.project_id}`}
                                    onClick={() => drillProject(p.project_id)}
                                    className="border-t hover:bg-[#FCFBF9] transition-colors cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                    <td className="px-5 py-3 font-bold text-[#9A968F]">{i + 1}</td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <div className="font-semibold text-[#1F1F1F] flex items-center gap-1.5">
                                                    {p.reference ? <span className="text-[11px] text-[#9A968F] font-mono">{p.reference}</span> : null}
                                                    {p.name}
                                                    <ArrowUpRight className="w-3 h-3 text-[#C7C2BB]" />
                                                </div>
                                                <div className="text-[11px]" style={{ color: "#6F6A64" }}>{p.client_name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="inline-block px-2 py-0.5 rounded-[5px] text-[10.5px] font-semibold" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{p.stage_label}</span>
                                    </td>
                                    <td className="px-3 py-3 text-right text-[#2D2D2D]">{p.open_actions}</td>
                                    <td className="px-3 py-3 text-right font-semibold" style={{ color: p.overdue_actions ? "#B33A2E" : "#6F6A64" }}>{p.overdue_actions}</td>
                                    <td className="px-3 py-3 text-right text-[#2D2D2D]">{p.open_incidents}</td>
                                    <td className="px-3 py-3 text-right font-semibold" style={{ color: p.riddor_open ? "#B33A2E" : "#6F6A64" }}>{p.riddor_open}</td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1.5 rounded-full bg-[#EFEAE4] overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${p.completion_rate}%`, background: p.completion_rate >= 60 ? "#3F7D3A" : p.completion_rate >= 30 ? "#C68A1C" : "#B33A2E" }} />
                                            </div>
                                            <span className="text-[11px] font-semibold w-8 text-right" style={{ color: "#6F6A64" }}>{p.completion_rate}%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {data.project_league.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-8 text-center italic text-[#9A968F]">No project data yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                )}
            </div>
        </div>
    );
}

const TT = { fontSize: 12, borderRadius: 8, border: "1px solid #E5E0DA", boxShadow: "0 4px 14px rgba(0,0,0,.08)" };

const TONES = {
    accent: { bg: "#FBEFE5", fg: "#9F4212" }, green: { bg: "#E8F1E6", fg: "#3F7D3A" },
    amber: { bg: "#FCF1DC", fg: "#C68A1C" }, red: { bg: "#FBE8E5", fg: "#B33A2E" },
    neutral: { bg: "#F0EDE8", fg: "#6F6A64" },
};

function HeadlineCard({ testid, icon: Icon, label, value, delta, tone = "neutral", invertDelta }) {
    const t = TONES[tone] || TONES.neutral;
    const hasDelta = delta !== undefined && delta !== null;
    const positive = invertDelta ? delta < 0 : delta > 0;
    const DeltaIcon = delta === 0 ? Minus : positive ? TrendingUp : TrendingDown;
    const deltaColor = delta === 0 ? "#9A968F" : positive ? "#3F7D3A" : "#B33A2E";
    return (
        <div data-testid={testid} className="bg-white border rounded-[11px] p-4 transition-all hover:shadow-brand hover:-translate-y-px" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center justify-between mb-3">
                <div className="w-[34px] h-[34px] rounded-[9px] grid place-items-center" style={{ background: t.bg, color: t.fg }}>
                    <Icon className="w-[17px] h-[17px]" />
                </div>
                {hasDelta && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold" style={{ color: deltaColor }}>
                        <DeltaIcon className="w-3 h-3" /> {delta > 0 ? `+${delta}` : delta} vs last mo
                    </span>
                )}
            </div>
            <div className="font-display font-bold text-[28px] leading-none tracking-tight text-[#1F1F1F]">{value}</div>
            <div className="text-[11.5px] mt-1.5 font-medium" style={{ color: "#6F6A64" }}>{label}</div>
        </div>
    );
}

function ChartCard({ testid, title, subtitle, children }) {
    return (
        <div data-testid={testid} className="bg-white border rounded-[11px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
            <div className="mb-3">
                <h3 className="text-[14px] font-semibold text-[#1F1F1F]">{title}</h3>
                {subtitle && <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function DonutCard({ testid, title, pairs, onClick }) {
    const chartData = useMemo(() => pairs.map(([name, value]) => ({ name, value })), [pairs]);
    const total = chartData.reduce((s, d) => s + d.value, 0);
    return (
        <ChartCard testid={testid} title={title} subtitle={`${total} total · click to open`}>
            <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                    <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2} cursor="pointer" onClick={onClick}>
                        {chartData.map((d, i) => <Cell key={d.name} fill={STATUS_PALETTE[i % STATUS_PALETTE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={TT} />
                    <Legend wrapperStyle={{ fontSize: 10.5 }} iconSize={8} />
                </PieChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}
